import type { LucideIcon } from 'lucide-react'
import { useMatches, useRouter } from '@tanstack/react-router'

/**
 * Screen metadata lives on each route as `staticData.screen`. The sidebar,
 * the command palette and every page header are built from it, so adding a
 * screen is: create the route file with its `staticData.screen`. Nothing else.
 *
 * Paths by scope:
 *   /w/$ws/<route>   Workspace, Investigate (Trace), Improve — one workspace
 *   /me/<route>      Me
 *   /org/<route>     Organisation (members, roles, usage, budgets…)
 *   /billing/<route> Organisation › billing
 *   /connect, /develop, /govern, /help, /platform — organisation-wide
 *   /<route>         public pages (login, signup, legal, status…)
 */
export const GROUPS = ['Me', 'Workspace', 'Investigate', 'Improve', 'Connect', 'Organisation', 'Govern', 'Develop', 'Help', 'Platform'] as const
export type Group = (typeof GROUPS)[number]

export const GROUP_LABEL: Record<Group, string> = {
  Me: 'Me',
  Workspace: 'Workspace',
  Investigate: 'Trace',
  Improve: 'Improve',
  Connect: 'Connect',
  Organisation: 'Org',
  Govern: 'Govern',
  Develop: 'Develop',
  Help: 'Help',
  Platform: 'Platform',
}

export interface ScreenMeta {
  /** Spec screen id, SCR-001…SCR-062, or SCR-1xx for screens added beyond the spec. */
  id: string
  title: string
  group: Group
  icon: LucideIcon
  /** Needs ANY of these permissions to show in navigation (the API still decides). */
  perm?: string | string[]
  /** Detail/flow screens stay out of the sidebar. */
  hidden?: boolean
  /** Listed first in its group; the rail button opens it. */
  pin?: boolean
  eyebrow?: string
  description?: string
  /** Sort within the group (lower first). */
  order?: number
  /** Super admins only (Platform). */
  platform?: boolean
}

declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    screen?: ScreenMeta
  }
}

export interface NavScreen extends ScreenMeta {
  fullPath: string
  routeId: string
}

/** Every screen registered in the route tree. */
export function useAllScreens(): NavScreen[] {
  const router = useRouter()
  const out: NavScreen[] = []
  for (const r of Object.values(router.routesById) as Array<{ id: string; fullPath: string; options: { staticData?: { screen?: ScreenMeta } } }>) {
    const s = r.options.staticData?.screen
    if (s) out.push({ ...s, fullPath: r.fullPath, routeId: r.id })
  }
  return out.sort((a, b) => Number(!!b.pin) - Number(!!a.pin) || (a.order ?? 100) - (b.order ?? 100) || a.title.localeCompare(b.title))
}

/** The screen the person is on (deepest match carrying screen metadata). */
export function useCurrentScreen(): (ScreenMeta & { routeId: string }) | null {
  const matches = useMatches()
  for (let i = matches.length - 1; i >= 0; i--) {
    const m = matches[i] as { routeId: string; staticData?: { screen?: ScreenMeta } }
    if (m.staticData?.screen) return { ...m.staticData.screen, routeId: m.routeId }
  }
  return null
}
